// src/hooks/useApi.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { apiClient } from '../services/api'; // Perbaikan import

export const useApi = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const fetchData = useCallback(async (customUrl = url, customOptions = {}) => {
    // Validasi URL - pastikan ini string, bukan fungsi
    if (!customUrl || typeof customUrl !== 'string') {
      console.error('useApi: URL must be a string, received:', typeof customUrl, customUrl);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const config = {
        ...options,
        ...customOptions,
        headers: {
          ...options.headers,
          ...customOptions.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      // Gunakan apiClient langsung
      let response;
      const method = (config.method || 'GET').toLowerCase();

      switch (method) {
        case 'get':
          response = await apiClient.get(customUrl, { 
            params: config.params,
            headers: config.headers 
          });
          break;
        case 'post':
          response = await apiClient.post(customUrl, config.data, { 
            headers: config.headers 
          });
          break;
        case 'put':
          response = await apiClient.put(customUrl, config.data, { 
            headers: config.headers 
          });
          break;
        case 'delete':
          response = await apiClient.delete(customUrl, { 
            headers: config.headers 
          });
          break;
        default:
          response = await apiClient.get(customUrl, { 
            params: config.params,
            headers: config.headers 
          });
      }

      setData(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      console.error('API Error:', {
        url: customUrl,
        method: config.method || 'GET',
        status: err.response?.status,
        message: errorMessage,
        baseURL: err.config?.baseURL
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [url, token, options]);

  const mutate = useCallback(async (mutateUrl, mutateOptions = {}) => {
    return await fetchData(mutateUrl, mutateOptions);
  }, [fetchData]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (url && !options.manual && typeof url === 'string') {
      fetchData();
    }
  }, [url, fetchData, options.manual]);

  return {
    data,
    loading,
    error,
    refetch,
    mutate
  };
};

// Hook khusus untuk GET request
export const useGet = (url, options = {}) => {
  return useApi(url, { ...options, method: 'GET' });
};

// Hook khusus untuk POST request
export const usePost = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const post = useCallback(async (url, data, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const config = {
        ...options,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { post, loading, error };
};

// Hook khusus untuk PUT request
export const usePut = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const put = useCallback(async (url, data, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const config = {
        ...options,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { put, loading, error };
};

// Hook khusus untuk DELETE request
export const useDelete = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const deleteItem = useCallback(async (url, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const config = {
        ...options,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { delete: deleteItem, loading, error };
};

// Hook untuk multiple API calls
export const useMultipleApi = (requests = []) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const fetchMultiple = useCallback(async () => {
    if (!requests.length) return;

    setLoading(true);
    setError(null);

    try {
      const promises = requests.map(({ url, options = {} }) => {
        const config = {
          ...options,
          headers: {
            ...options.headers,
            ...(token && { Authorization: `Bearer ${token}` })
          }
        };

        const method = (config.method || 'GET').toLowerCase();
        
        switch (method) {
          case 'get':
            return apiClient.get(url, { 
              params: config.params,
              headers: config.headers 
            });
          case 'post':
            return apiClient.post(url, config.data, { 
              headers: config.headers 
            });
          case 'put':
            return apiClient.put(url, config.data, { 
              headers: config.headers 
            });
          case 'delete':
            return apiClient.delete(url, { 
              headers: config.headers 
            });
          default:
            return apiClient.get(url, { 
              params: config.params,
              headers: config.headers 
            });
        }
      });

      const responses = await Promise.all(promises);
      const result = {};
      
      responses.forEach((response, index) => {
        const key = requests[index].key || `request_${index}`;
        result[key] = response.data;
      });

      setData(result);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [requests, token]);

  useEffect(() => {
    fetchMultiple();
  }, [fetchMultiple]);

  return {
    data,
    loading,
    error,
    refetch: fetchMultiple
  };
};

export default useApi;